import type { MouseEvent } from "react";
import { Anchor, Badge, Button, Group, HoverCard, Stack, Text } from "@mantine/core";

export interface UserHoverCardProps {
  /** Display name — the trigger text and the dropdown heading. */
  name: string;
  /** Secondary detail shown under the name (e.g. the user's email). */
  email?: string | null;
  /** Small affiliation badge in the dropdown (e.g. "Owner", "Staff", "AI"). */
  role?: string | null;
  /**
   * SPA navigation to the user's detail page. When provided, the trigger name
   * becomes interactive and a "Go to details" action appears in the dropdown.
   * Prefer this over ``detailHref`` inside a router so navigation stays
   * client-side.
   */
  onViewDetails?: () => void;
  /** Plain-href alternative to ``onViewDetails`` (causes a full navigation). */
  detailHref?: string;
  /** Label for the details action. Default: "Go to details". */
  detailLabel?: string;
  /** Trigger font size token. Default: "xs". */
  size?: string | number;
  /** Trigger text color token. Default: "dimmed". */
  color?: string;
  /** Trigger font weight. */
  fw?: number;
}

/**
 * An author/user chip with a hover card. The trigger shows the name; hovering
 * reveals email + an optional role badge, and (in apps that pass navigation) a
 * "Go to details" link to the user's profile.
 *
 * Router-agnostic: it lives in ``@nodus/ui`` and takes either an
 * ``onViewDetails`` callback (SPA navigation — preferred) or a plain
 * ``detailHref``. With neither, it's a read-only info card — the member portal
 * uses that mode to show who a colleague is without linking anywhere.
 */
export function UserHoverCard({
  name,
  email,
  role,
  onViewDetails,
  detailHref,
  detailLabel = "Go to details",
  size = "xs",
  color = "dimmed",
  fw,
}: UserHoverCardProps) {
  // SPA-navigate on a plain left click, but let modified clicks / middle-click
  // fall through to the anchor's href so operators can open the profile in a
  // new tab. Only meaningful when both an href and a callback are supplied.
  const handleNavClick = (e: MouseEvent) => {
    if (!onViewDetails) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onViewDetails();
  };

  let trigger;
  if (detailHref) {
    // Real link → new-tab capable; intercept plain clicks for SPA nav.
    trigger = (
      <Anchor href={detailHref} onClick={handleNavClick} fz={size} c={color} fw={fw}>
        {name}
      </Anchor>
    );
  } else if (onViewDetails) {
    trigger = (
      <Anchor component="button" type="button" onClick={onViewDetails} fz={size} c={color} fw={fw}>
        {name}
      </Anchor>
    );
  } else {
    trigger = (
      <Text component="span" fz={size} c={color} fw={fw} style={{ cursor: "default" }}>
        {name}
      </Text>
    );
  }

  return (
    <HoverCard width={240} shadow="md" openDelay={150} closeDelay={80} withinPortal position="top-start">
      <HoverCard.Target>{trigger}</HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={6}>
          <Group gap="xs" justify="space-between" wrap="nowrap">
            <Text fz="sm" fw={600} style={{ wordBreak: "break-word" }}>
              {name}
            </Text>
            {role ? (
              <Badge size="xs" variant="light" color="gray">
                {role}
              </Badge>
            ) : null}
          </Group>
          {email ? (
            <Text fz="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
              {email}
            </Text>
          ) : null}
          {detailHref ? (
            <Anchor href={detailHref} onClick={handleNavClick} fz="xs" mt={2}>
              {detailLabel}
            </Anchor>
          ) : onViewDetails ? (
            <Button size="compact-xs" variant="light" onClick={onViewDetails} mt={2}>
              {detailLabel}
            </Button>
          ) : null}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
